/// <amd-module name="Feed/Legacy/Protocols/NuGet/NuGetDataService" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";

import { NuGetPackagesBatchRequest } from "Feed/Common/Protocols/NuGet/Contracts/NuGet";
import * as VSS_NuGet_Contracts from "Feed/Common/Protocols/NuGet/Contracts/NuGet";
import { INuGetRestClient } from "Feed/Common/Protocols/NuGet/RestClient/NuGet";
import { UpstreamingBehavior } from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { BatchExecutor, IBatchIterator } from "Feed/Legacy/Protocols/Common/BatchHandling";
import { MinimalPackageDetails } from "Feed/Legacy/Protocols/Common/WebApi/PackagingShared.Contracts";
import * as NuGetPackageDetails from "Feed/Legacy/Protocols/NuGet/NuGetPackageDetails";
import { INuGetRestApiUrlGeneratorService } from "Feed/Legacy/Protocols/NuGet/NuGetRestApiUrlGeneratorService";

export interface IFeedNuGetDataService extends IVssService {
    batchSize: number;
    updatePackageVersions(
        nuGetBatchRequest: NuGetPackagesBatchRequest,
        projectId: string,
        feedId: string
    ): Promise<void>;
    updatePackageVersion(
        nugetPackageVersionDetails: NuGetPackageDetails.PackageVersionDetails,
        projectId: string,
        feedId: string,
        packageName: string,
        packageVersion: string
    ): Promise<void>;
    getDownloadUrl(fileName: string, includeProject: boolean): Promise<string>;
    getDownloadPackageVersionContentUrl(
        feedId: string,
        packageName: string,
        packageVersion: string,
        includeProject: boolean
    ): Promise<string>;
    batchDelete(projectId: string, feedId: string, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void>;
    batchRestoreToFeed(
        projectId: string,
        feedId: string,
        iterator: IBatchIterator<MinimalPackageDetails[]>
    ): Promise<void>;
    batchPermanentlyDelete(
        projectId: string,
        feedId: string,
        iterator: IBatchIterator<MinimalPackageDetails[]>
    ): Promise<void>;
    batchPromotePackages(
        projectId: string,
        feedId: string,
        viewId: string,
        iterator: IBatchIterator<MinimalPackageDetails[]>
    ): Promise<void[]>;
    getUpstreamingBehavior(feedId: string, packageName: string, projectId: string): Promise<UpstreamingBehavior>;
    setUpstreamingBehavior(
        feedId: string,
        packageName: string,
        behavior: UpstreamingBehavior,
        projectId: string
    ): Promise<void>;
}

class NuGetDataService extends VssService implements IFeedNuGetDataService {
    get NuGetRestClient(): INuGetRestClient {
        return this.pageContext.getRestClient<INuGetRestClient>("INuGetRestClient");
    }

    get NuGetRestApiUrlGeneratorService(): INuGetRestApiUrlGeneratorService {
        return this.pageContext.getService<INuGetRestApiUrlGeneratorService>("INuGetRestApiUrlGeneratorService");
    }

    get batchSize() {
        return this.pageContext
            .getService<HubWebPageDataService>("IFeedWebPageDataService")
            .getBatchOperationPageSize();
    }

    public updatePackageVersions(
        nuGetBatchRequest: NuGetPackagesBatchRequest,
        projectId: string,
        feedId: string
    ): Promise<void> {
        return this.NuGetRestClient.updatePackageVersions(nuGetBatchRequest, feedId, projectId);
    }

    public updatePackageVersion(
        nugetPackageVersionDetails: NuGetPackageDetails.PackageVersionDetails,
        projectId: string,
        feedId: string,
        packageName: string,
        packageVersion: string
    ): Promise<void> {
        return this.NuGetRestClient.updatePackageVersion(
            nugetPackageVersionDetails,
            feedId,
            packageName,
            packageVersion,
            projectId
        );
    }

    public getDownloadUrl(fileName: string, includeProject: boolean): Promise<string> {
        const url: string = this.NuGetRestApiUrlGeneratorService.getDownloadClientUrl(fileName, includeProject);
        return Promise.resolve(url);
    }

    public getDownloadPackageVersionContentUrl(
        feedId: string,
        packageName: string,
        packageVersion: string,
        includeProject: boolean = false
    ): Promise<string> {
        const url: string = this.NuGetRestApiUrlGeneratorService.getDownloadPackageVersionContentUrl(
            feedId,
            packageName,
            packageVersion,
            includeProject
        );
        return Promise.resolve(url);
    }

    public async batchDelete(
        projectId: string,
        feedId: string,
        iterator: IBatchIterator<MinimalPackageDetails[]>
    ): Promise<void> {
        await BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <NuGetPackagesBatchRequest>{
                data: null,
                operation: VSS_NuGet_Contracts.NuGetBatchOperationType.Delete,
                packages: packageDetails
            };
            await this.NuGetRestClient.updatePackageVersions(batchRequest, feedId, projectId);
        });
    }

    public async batchRestoreToFeed(
        projectId: string,
        feedId: string,
        iterator: IBatchIterator<MinimalPackageDetails[]>
    ): Promise<void> {
        await BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <NuGetPackagesBatchRequest>{
                data: null,
                operation: VSS_NuGet_Contracts.NuGetBatchOperationType.RestoreToFeed,
                packages: packageDetails
            };
            await this.NuGetRestClient.updateRecycleBinPackageVersions(batchRequest, feedId, projectId);
        });
    }

    public async batchPermanentlyDelete(
        projectId: string,
        feedId: string,
        iterator: IBatchIterator<MinimalPackageDetails[]>
    ): Promise<void> {
        await BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <NuGetPackagesBatchRequest>{
                data: null,
                operation: VSS_NuGet_Contracts.NuGetBatchOperationType.PermanentDelete,
                packages: packageDetails
            };
            await this.NuGetRestClient.updateRecycleBinPackageVersions(batchRequest, feedId, projectId);
        });
    }

    public batchPromotePackages(
        projectId: string,
        feedId: string,
        viewId: string,
        iterator: IBatchIterator<MinimalPackageDetails[]>
    ): Promise<void[]> {
        return BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <NuGetPackagesBatchRequest>{
                data: { viewId },
                operation: VSS_NuGet_Contracts.NuGetBatchOperationType.Promote,
                packages: packageDetails
            };
            await this.NuGetRestClient.updatePackageVersions(batchRequest, feedId, projectId);
        });
    }

    public getUpstreamingBehavior(
        feedId: string,
        packageName: string,
        projectId: string
    ): Promise<UpstreamingBehavior> {
        return this.NuGetRestClient.getUpstreamingBehavior(feedId, packageName, projectId);
    }

    public setUpstreamingBehavior(
        feedId: string,
        packageName: string,
        behavior: UpstreamingBehavior,
        projectId: string
    ): Promise<void> {
        return this.NuGetRestClient.setUpstreamingBehavior(feedId, packageName, behavior, projectId);
    }
}

Services.add("IFeedNuGetDataService", { serviceFactory: NuGetDataService });
